import type { ReactNode } from "react";
import { DownloadIcon, PauseIcon, PlayIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";
import type { CloudEnvironmentLifecycleProjection } from "../../environments/primary/cloudEnvironmentLifecycleProjection";
import type { CmmdForgeServerMaintenance } from "../../environments/primary/cloudProjectCatalogContracts";

export type CloudRuntimeActionKind = "start" | "stop" | "recover" | "update";

export interface CloudRuntimeActionDescriptor {
  readonly kind: CloudRuntimeActionKind;
  readonly label: string;
  readonly busyLabel?: string;
}

export const PAUSE_ENVIRONMENT_LABEL = "Pause environment";
const WAKE_NOW_LABEL = "Wake now";
const RESUME_ENVIRONMENT_LABEL = "Resume";
export const RECOVER_ENVIRONMENT_LABEL = "Recover environment";
export const REPAIR_LABEL = "Repair checkout";
export const REMOVE_CHECKOUT_LABEL = "Remove checkout";
export const UPDATE_FORGE_LABEL = "Force update and repair";
export const DELETE_ENVIRONMENT_LABEL = "Delete environment";

const PAUSE_ACTION: CloudRuntimeActionDescriptor = {
  kind: "stop",
  label: PAUSE_ENVIRONMENT_LABEL,
  busyLabel: "Pausing...",
};
const RECOVER_ACTION: CloudRuntimeActionDescriptor = {
  kind: "recover",
  label: RECOVER_ENVIRONMENT_LABEL,
  busyLabel: "Recovering...",
};
const UPDATE_ACTION: CloudRuntimeActionDescriptor = {
  kind: "update",
  label: UPDATE_FORGE_LABEL,
  busyLabel: "Stopping turns and repairing...",
};
const ACTIONS_BY_LIFECYCLE: Readonly<
  Record<CloudEnvironmentLifecycleProjection["state"], readonly CloudRuntimeActionDescriptor[]>
> = {
  provisioning: [PAUSE_ACTION],
  sleeping: [],
  starting: [PAUSE_ACTION],
  ready: [PAUSE_ACTION, RECOVER_ACTION],
  unavailable: [],
  degraded: [RECOVER_ACTION],
  "needs-attention": [RECOVER_ACTION],
  paused: [{ kind: "start", label: RESUME_ENVIRONMENT_LABEL, busyLabel: "Resuming..." }],
  deleting: [],
  unknown: [RECOVER_ACTION],
};

export function getCloudRuntimeActions(input: {
  readonly maintenance: CmmdForgeServerMaintenance | undefined;
  readonly lifecycle: CloudEnvironmentLifecycleProjection;
}): readonly CloudRuntimeActionDescriptor[] {
  const actions = actionsForLifecycle(input.lifecycle);
  return input.lifecycle.state === "ready" || input.lifecycle.state === "sleeping"
    ? [UPDATE_ACTION, ...actions]
    : actions;
}

function actionsForLifecycle(
  lifecycle: CloudEnvironmentLifecycleProjection,
): readonly CloudRuntimeActionDescriptor[] {
  if (lifecycle.state !== "sleeping") return ACTIONS_BY_LIFECYCLE[lifecycle.state];
  return lifecycle.automaticWakeAllowed
    ? [{ kind: "start", label: WAKE_NOW_LABEL, busyLabel: "Waking..." }]
    : [];
}

export function cloudRuntimeActionIcon(kind: CloudRuntimeActionKind): ReactNode {
  switch (kind) {
    case "start":
      return <PlayIcon aria-hidden="true" className="size-3.5" />;
    case "stop":
      return <PauseIcon aria-hidden="true" className="size-3.5" />;
    case "recover":
      return <RotateCcwIcon aria-hidden="true" className="size-3.5" />;
    case "update":
      return <DownloadIcon aria-hidden="true" className="size-3.5" />;
  }
}

export function deleteEnvironmentIcon(): ReactNode {
  return <Trash2Icon aria-hidden="true" className="size-3.5" />;
}
